// ============================================================================
// BOQ — Project items XLSX import / export
// ============================================================================
// exportItemsToXlsx(items, zonesById) → returns ArrayBuffer for download
// parseItemsFromXlsx(file, { versionsByFullCode, zonesByCode }) →
//   returns { rows, errors }: parsed rows (one per xlsx row) with the
//   resolved item_version_id, project_zone_id, and per-row error messages.
//
// Column convention (must match what export produces so xlsx round-trips):
//   A: Code        — item_version full_code, e.g. MA.EXT.0014.a
//   B: Description — descriptive text (library default if blank, else override)
//   C: Unit        — read-only, for reference only on import
//   D: Quantity    — number (required)
//   E: Zone        — zone code (top-level or "parent/child"); blank = unzoned
//   F: Order       — sequence_order (optional)
// ============================================================================
import ExcelJS from 'exceljs';
import { effectiveUnit } from './measureUnits';

const BRIO_GOLD = 'FF9F902A';
const BRIO_GOLD_LIGHT = 'FFF5F0D0';
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRIO_GOLD } };
const THIN = { style: 'thin', color: { argb: 'FFD1D5DB' } };
const CELL_BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

const COLUMNS = [
  { key: 'code',        header: 'Code',        width: 18 },
  { key: 'description', header: 'Description', width: 60 },
  { key: 'unit',        header: 'Unit',        width: 10 },
  { key: 'quantity',    header: 'Quantity',     width: 12 },
  { key: 'rateQuoted',  header: 'Rate estimate',   width: 13 },
  { key: 'amtQuoted',   header: 'Amount estimate', width: 15 },
  { key: 'rateBilled',  header: 'Rate tender',     width: 13 },
  { key: 'amtBilled',   header: 'Amount tender',   width: 15 },
  { key: 'zone',        header: 'Zone',         width: 24 },
  { key: 'order',       header: 'Order',        width: 8  },
];

// Build the zone path for a row (used in both export and import). Walks up
// the parent chain to produce e.g. "villa-1 / floor-2 / br-1".
function buildZonePath(zoneId, zonesById) {
  if (!zoneId) return '';
  const parts = [];
  let cursor = zonesById.get(zoneId);
  while (cursor) {
    parts.unshift(cursor.code);
    cursor = cursor.parentId ? zonesById.get(cursor.parentId) : null;
  }
  return parts.join(' / ');
}

export async function exportItemsToXlsx({ projectName, projectCode, currency, items, zonesById }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'BRIO QS Pro';
  wb.created = new Date();
  const ws = wb.addWorksheet('BOQ items', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  ws.columns = COLUMNS;
  // Header row
  const headerRow = ws.getRow(1);
  headerRow.values = COLUMNS.map((c) => c.header);
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = HEADER_FILL;
  headerRow.alignment = { vertical: 'middle' };
  headerRow.height = 22;

  items.forEach((it, i) => {
    const r = i + 2; // data starts on row 2
    ws.addRow({
      code: it.version?.fullCode || '',
      description: it.descriptionOverride || it.version?.shortDescription || '',
      unit: effectiveUnit(it),
      quantity: Number(it.quantity),
      rateQuoted: it.rateQuoted == null ? null : Number(it.rateQuoted),
      // Amount quoted = Quantity (D) × Rate quoted (E)
      amtQuoted: { formula: `D${r}*E${r}`, result: it.amountQuoted == null ? null : Number(it.amountQuoted) },
      rateBilled: it.rateBilled == null ? null : Number(it.rateBilled),
      // Amount billed = Quantity (D) × Rate billed (G)
      amtBilled: { formula: `D${r}*G${r}`, result: it.amountBilled == null ? null : Number(it.amountBilled) },
      zone: buildZonePath(it.projectZoneId, zonesById),
      order: it.sequenceOrder ?? 0,
    });
  });

  // Apply borders + numeric formatting
  const lastRow = ws.rowCount;
  // INR uses Indian lakh/crore grouping; others Western. Rates/amounts: 0 dp.
  const moneyFmt = (currency || 'INR') === 'INR'
    ? '[>=10000000]#,##,##,##0;[>=100000]#,##,##0;#,##0'
    : '#,##0';
  // Quantity rule: integer when |value| >= 10, up to 2 dp when < 10.
  const qtyFmt = '[<10]#,##0.##;[>-10]-#,##0.##;#,##0';
  if (lastRow > 1) {
    for (let r = 2; r <= lastRow; r++) {
      ws.getRow(r).eachCell((cell) => { cell.border = CELL_BORDER; });
      ws.getCell(r, 4).numFmt = qtyFmt;        // Quantity
      ws.getCell(r, 5).numFmt = moneyFmt;      // Rate quoted
      ws.getCell(r, 6).numFmt = moneyFmt;      // Amount quoted
      ws.getCell(r, 7).numFmt = moneyFmt;      // Rate billed
      ws.getCell(r, 8).numFmt = moneyFmt;      // Amount billed
    }
    // Total row: sum quoted (F) and billed (H)
    const totalRow = lastRow + 1;
    ws.getCell(totalRow, 4).value = 'Total';
    ws.getCell(totalRow, 4).font = { bold: true };
    ws.getCell(totalRow, 6).value = { formula: `SUM(F2:F${lastRow})` };
    ws.getCell(totalRow, 8).value = { formula: `SUM(H2:H${lastRow})` };
    [6, 8].forEach((c) => { ws.getCell(totalRow, c).font = { bold: true }; ws.getCell(totalRow, c).numFmt = moneyFmt; });
    // Light-gold zebra
    for (let r = 2; r <= lastRow; r += 2) {
      ws.getRow(r).fill = {
        type: 'pattern', pattern: 'solid',
        fgColor: { argb: BRIO_GOLD_LIGHT },
      };
    }
  }

  // Metadata sheet for traceability
  const meta = wb.addWorksheet('Meta');
  meta.addRow(['Project', projectName || '']);
  meta.addRow(['Project code', projectCode || '']);
  meta.addRow(['Currency', currency || 'INR']);
  meta.addRow(['Exported at', new Date().toISOString()]);
  meta.addRow(['Row count', items.length]);
  meta.columns = [{ width: 18 }, { width: 60 }];

  return wb.xlsx.writeBuffer();
}

export async function parseItemsFromXlsx(file, { versionsByFullCode, zonesByPath }) {
  const buffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);
  const ws = wb.getWorksheet('BOQ items') || wb.worksheets[0];
  if (!ws) {
    return { rows: [], errors: [{ row: 0, message: 'No worksheet found.' }] };
  }

  // Resolve column letters from the header row
  const headerRow = ws.getRow(1);
  const headerMap = {};
  headerRow.eachCell((cell, col) => {
    const label = String(cell.value || '').trim().toLowerCase();
    if (label) headerMap[label] = col;
  });

  const colFor = (name) => headerMap[name.toLowerCase()];
  const codeCol = colFor('code');
  const descCol = colFor('description');
  const qtyCol = colFor('quantity');
  const rateQuotedCol = colFor('rate estimate') || colFor('rate quoted') || colFor('rate');
  const rateBilledCol = colFor('rate tender') || colFor('rate billed');
  const zoneCol = colFor('zone');
  const orderCol = colFor('order');

  if (!codeCol || !qtyCol) {
    return {
      rows: [],
      errors: [{ row: 1, message: 'Required columns missing — sheet must have Code and Quantity headers.' }],
    };
  }

  const rows = [];
  const errors = [];
  const lastRow = ws.rowCount;

  for (let r = 2; r <= lastRow; r++) {
    const row = ws.getRow(r);
    if (!row.hasValues) continue;

    const cellText = (col) => {
      if (!col) return '';
      const v = row.getCell(col).value;
      if (v === null || v === undefined) return '';
      if (typeof v === 'object' && 'result' in v) return String(v.result ?? '').trim();
      if (typeof v === 'object' && 'text' in v) return String(v.text ?? '').trim();
      if (typeof v === 'object' && 'richText' in v) {
        return v.richText.map((p) => p.text).join('').trim();
      }
      return String(v).trim();
    };
    const cellNumber = (col) => {
      const txt = cellText(col);
      if (txt === '') return null;
      const cleaned = txt.replace(/[,$€£₹\s]/g, '');
      const n = Number(cleaned);
      return isNaN(n) ? null : n;
    };

    const code = cellText(codeCol).toUpperCase();
    if (!code) continue; // Skip totally empty rows

    const rowErrors = [];
    const version = versionsByFullCode.get(code);
    if (!version) {
      rowErrors.push(`Unknown library code "${code}" — create the library item first.`);
    }

    const qty = cellNumber(qtyCol);
    if (qty === null) rowErrors.push('Quantity must be a number.');
    else if (qty < 0) rowErrors.push('Quantity cannot be negative.');

    const zonePath = cellText(zoneCol);
    let zoneId = null;
    if (zonePath) {
      zoneId = zonesByPath.get(zonePath) || null;
      if (!zoneId) {
        rowErrors.push(`Unknown zone "${zonePath}" — leave blank or create the zone first.`);
      }
    }

    const order = orderCol ? cellNumber(orderCol) : null;
    const desc = descCol ? cellText(descCol) : '';
    const rateQuoted = rateQuotedCol ? cellNumber(rateQuotedCol) : null;
    const rateBilled = rateBilledCol ? cellNumber(rateBilledCol) : null;
    if (rateQuoted !== null && rateQuoted < 0) rowErrors.push('Quoted rate cannot be negative.');
    if (rateBilled !== null && rateBilled < 0) rowErrors.push('Billed rate cannot be negative.');

    const descriptionOverride = desc && desc !== (version?.shortDescription || '') ? desc : null;

    rows.push({
      rowNumber: r,
      code,
      itemVersionId: version?.id || null,
      libraryDescription: version?.shortDescription || '',
      libraryUnit: version?.unit || '',
      quantity: qty,
      rateQuoted,
      rateBilled,
      projectZoneId: zoneId,
      zonePath,
      descriptionOverride,
      sequenceOrder: order ?? 0,
      errors: rowErrors,
    });
    if (rowErrors.length) errors.push({ row: r, message: rowErrors.join(' · ') });
  }

  return { rows, errors };
}

// Parse pasted text (tab- or comma-separated) into the same row shape as
// parseItemsFromXlsx. Touch-friendly bulk entry for iPad, where range
// clipboard / keyboard paste into the grid isn't available.
//
// Column order: Code, Quantity, [Zone], [Description].
// A header row ("code", "quantity"...) is detected and skipped.
export function parsePastedRows(text, { versionsByFullCode, zonesByPath }) {
  const rows = [];
  const errors = [];
  const lines = String(text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  const splitLine = (line) => (line.includes('\t') ? line.split('\t') : line.split(','))
    .map((c) => c.trim());

  lines.forEach((line, idx) => {
    const cells = splitLine(line);
    const first = (cells[0] || '').toUpperCase();

    // Skip a header row if present
    if (idx === 0 && (first === 'CODE' || first === 'ITEM CODE')) return;
    if (!first) return;

    const rowNumber = idx + 1;
    const rowErrors = [];

    const code = first;
    const version = versionsByFullCode.get(code);
    if (!version) rowErrors.push(`Unknown library code "${code}".`);

    const rawQty = cells[1] ?? '';
    const cleanedQty = String(rawQty).replace(/[,$€£₹\s]/g, '');
    const qty = cleanedQty === '' ? null : Number(cleanedQty);
    if (qty === null || isNaN(qty)) rowErrors.push('Quantity must be a number.');
    else if (qty < 0) rowErrors.push('Quantity cannot be negative.');

    const zonePath = (cells[2] || '').trim();
    let zoneId = null;
    if (zonePath) {
      zoneId = zonesByPath.get(zonePath) || null;
      if (!zoneId) rowErrors.push(`Unknown zone "${zonePath}".`);
    }

    const desc = (cells[3] || '').trim();
    const descriptionOverride = desc && desc !== (version?.shortDescription || '') ? desc : null;

    rows.push({
      rowNumber,
      code,
      itemVersionId: version?.id || null,
      libraryDescription: version?.shortDescription || '',
      libraryUnit: version?.unit || '',
      quantity: qty,
      projectZoneId: zoneId,
      zonePath,
      descriptionOverride,
      sequenceOrder: 0,
      errors: rowErrors,
    });
    if (rowErrors.length) errors.push({ row: rowNumber, message: rowErrors.join(' · ') });
  });

  return { rows, errors };
}

// Build the lookups callers need before parseItemsFromXlsx
export function buildXlsxLookups({ versions, zonesById }) {
  const versionsByFullCode = new Map();
  versions.forEach((v) => versionsByFullCode.set(v.fullCode, v));

  const zonesByPath = new Map();
  const flatten = (id) => {
    const z = zonesById.get(id);
    if (!z) return '';
    const parts = [];
    let cursor = z;
    while (cursor) {
      parts.unshift(cursor.code);
      cursor = cursor.parentId ? zonesById.get(cursor.parentId) : null;
    }
    return parts.join(' / ');
  };
  zonesById.forEach((z) => {
    zonesByPath.set(flatten(z.id), z.id);
    // Also accept the leaf code on its own when unambiguous
    if (!zonesByPath.has(z.code)) zonesByPath.set(z.code, z.id);
  });

  return { versionsByFullCode, zonesByPath };
}
